import React from 'react'

import {difference, groupBy, intersection, noop, sortBy, uniq} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as shippingApi from '../../../services/api/shipping'
import {formDataToShippingRequests} from '../../forms/shippings/utils'
import {toValidationSchema} from '../../utils/form'

import {Country, CountryCode, ModalMeta} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import LabeledCheckbox from '../../../../../commons/src/components/labeled-checkbox-field'
import LinkButton from '../../../../../commons/src/components/link-button'
import Modal from '../../../../../commons/src/components/modal'
import CheckboxTreeLayout from '../../../components/checkbox-tree-layout'

import ShippingsForm, {
  SingleFormData,
  initialValues,
  validationSchema,
} from '../../forms/shippings/form'

type Props = {
  meta: ModalMeta
  onDone?: () => void
}

const toggleCountryCodes = (
  countryCodes: CountryCode[],
  countryCodesOfConcern: CountryCode[],
  value: boolean
) => {
  if (value) {
    return uniq([...countryCodes, ...countryCodesOfConcern])
  }

  return difference(countryCodes, countryCodesOfConcern)
}

const CreateShippingsModal = ({meta, onDone = noop}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  const countries = useAppSelector(state => state.core.countries)

  invariant(provider, 'Provider is missing.')
  invariant(countries, 'Countries missing.')

  const [showRegion, setShowRegion] = React.useState<Record<string, boolean>>({})

  const [regions, countriesWithoutRegion] = React.useMemo(
    () => [
      groupBy(
        Object.values(countries).filter(country => country.region),
        country => country.region
      ),
      sortBy(
        Object.values(countries).filter(country => !country.region),
        ['name']
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.values(countries)
  )

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const requests = formDataToShippingRequests(values, provider)

      try {
        await shippingApi.createMultiple(requests)

        onDone()
        dispatch(closeModal())
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const renderCountry = (country: Country) => (
    <LabeledCheckbox
      size="90"
      key={country.code}
      name="countryCodes"
      label={country.name}
      checked={formik.values.countryCodes.includes(country.code)}
      appearance={formik.submitCount > 0 && !!formik.errors.countryCodes ? 'error' : undefined}
      onChange={(value, name) =>
        formik.setFieldValue(
          name || '',
          toggleCountryCodes(formik.values.countryCodes, [country.code], value),
          true
        )
      }
      onBlur={formik.handleBlur}
    />
  )

  const renderRegion = (region: string) => {
    const countriesOfRegion = sortBy(regions[region], ['name'])
    const countryCodes = countriesOfRegion.map(country => country.code)
    const childrenVisible = showRegion[region]
    const numSelectedCountries = intersection(countryCodes, formik.values.countryCodes).length

    return (
      <CheckboxTreeLayout
        key={region}
        header={
          <LabeledCheckbox
            name="countryCodes"
            label={region}
            count={`${numSelectedCountries}/${countriesOfRegion.length}`}
            suffix={
              <LinkButton
                onClick={() => setShowRegion({...showRegion, [region]: !childrenVisible})}
              >
                {childrenVisible ? 'Hide all' : 'Show all'}
              </LinkButton>
            }
            appearance={
              formik.submitCount > 0 && !!formik.errors.countryCodes ? 'error' : undefined
            }
            checked={numSelectedCountries > 0}
            partial={numSelectedCountries < countriesOfRegion.length}
            onChange={(value, name) =>
              formik.setFieldValue(
                name || '',
                toggleCountryCodes(
                  formik.values.countryCodes,
                  countryCodes,
                  numSelectedCountries < countriesOfRegion.length || value
                ),
                true
              )
            }
            onBlur={formik.handleBlur}
          />
        }
      >
        {childrenVisible && countriesOfRegion.map(renderCountry)}
      </CheckboxTreeLayout>
    )
  }

  const renderRegions = () => {
    const regionsList = Object.keys(regions).sort()

    return (
      <FormRow layout="half-half">
        <FieldNote appearance="error" note={formik.submitCount > 0 && formik.errors.countryCodes}>
          {regionsList.slice(0, Math.ceil(regionsList.length / 2)).map(renderRegion)}
        </FieldNote>
        <div>
          {regionsList.slice(Math.ceil(regionsList.length / 2)).map(renderRegion)}
          {countriesWithoutRegion.map(renderCountry)}
        </div>
      </FormRow>
    )
  }

  return (
    <Modal
      size="l"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Create new shipping option" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ShippingsForm formik={formik}>
        <Headline size="s" label="Regions" className="u-margin-top-l" />
        {renderRegions()}
      </ShippingsForm>
    </Modal>
  )
}

export default CreateShippingsModal
